<template>
  <div
    v-if="filters && filters.length > 0"
    v-click-outside="vcoConfig"
    class="w-40 ml-0 mr-2"
  >
    <div
      class="h-8 text-sm p-2 border rounded border-solid border-color cursor-pointer bg-white"
      data-testid="filter-button"
      @click="toggleDropdown"
    >
      <div class="h-full flex justify-between items-center">
        <equalizer-icon class="h-4 w-4" />
        <div class="pl-2 grow">
          {{ $t('components.th_datatable.search_filter.filter.button') }}
        </div>
        <el-icon>
          <arrow-up-icon v-if="dropdownOpen" />
          <arrow-down-icon v-else />
        </el-icon>
      </div>
    </div>
    <div
      v-show="dropdownOpen"
      class="th-container border rounded-b absolute bg-white"
      :style="containerStyle"
      data-testid="filters-dropdown"
    >
      <div
        class="th-container-gap w-40 h-3 absolute bg-white border-solid border-r border-l"
      />
      <div
        ref="dropdownItemsRef"
        class="th-container-items"
        :style="dropdownItemsMaxHeightStyle"
      >
        <div
          class="filter-items px-6 pt-4 pb-0"
          :class="{ 'overflow-y-auto pr-5': filters.length >= 10 }"
        >
          <template v-if="showPillFilter">
            <div
              v-for="(group, category) in categorizedFilters"
              :key="category"
            >
              <h4
                v-if="category !== 'Uncategorized'"
                class="py-2 font-semibold cursor-pointer flex items-center justify-between"
                @click="toggleCategory(category)"
              >
                {{ $t(category) }}
                <el-icon>
                  <arrow-down-icon v-if="categoryState[category]" />
                  <arrow-right-icon v-else />
                </el-icon>
              </h4>
              <div v-show="categoryState[category]">
                <div
                  v-for="(filter, index) in group"
                  :key="index"
                  class="filter-item"
                >
                  <component
                    :is="componentMapping(filter.type)"
                    v-if="shouldRenderComponent(filter)"
                    v-model="tagsObject[filter.name].value"
                    v-bind="filter"
                    :i18n-config="i18nConfig"
                    :filter="filter"
                    :use-old="filter.useOld"
                    :section-title="tagsObject[filter.name].title"
                    :locale="locale"
                    :tags="tagsObject"
                    :append-to-body="filter.appendToBody"
                    @update:modelValue="dispatchFilter"
                    @invalid-filters="handleInvalidFilters"
                    @resource-set="(data) => handleInitialFetch(filter, data)"
                    @keyup.enter="handleEnter"
                    @submit="handleSubmit"
                  />
                </div>
              </div>
              <el-divider />
            </div>
          </template>
          <template v-else>
            <div
              v-for="(filter, index) in filters.filter(
                (item) => item.type || item.render
              )"
              :key="index"
              class="filter-item"
            >
              <component
                :is="componentMapping(filter.type)"
                v-if="shouldRenderComponent(filter)"
                v-model="tagsObject[filter.name].value"
                v-bind="filter"
                :i18n-config="i18nConfig"
                :filter="filter"
                :use-old="filter.useOld"
                :section-title="tagsObject[filter.name].title"
                :locale="locale"
                :tags="tagsObject"
                :append-to-body="filter.appendToBody"
                @update:modelValue="dispatchFilter"
                @invalid-filters="handleInvalidFilters"
                @resource-set="(data) => handleInitialFetch(filter, data)"
                @keyup.enter="handleEnter"
                @submit="handleSubmit"
              />
            </div>
          </template>
        </div>
        <div class="flex justify-end px-6 py-4 select-none shrink-0">
          <!-- Reset -->
          <el-button @click="reset">
            {{ $t('components.th_datatable.search_filter.search.reset') }}
          </el-button>
          <!-- Search -->
          <el-button
            type="accent"
            :disabled="searchDisabled"
            @click="handleSearchClick"
          >
            {{ $t('components.th_datatable.search_filter.search.button') }}
          </el-button>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import mapValues from 'just-map-values'
import compare from 'just-compare'
import { defineAsyncComponent, ref } from 'vue'
import {
  ArrowUpIcon,
  ArrowDownIcon,
  EqualizerIcon,
  ArrowRightIcon
} from '@shared/assets/icons'
import { useDropdownViewportMaxHeight } from '@shared/composables/use-dropdown-viewport-max-height'

export default {
  name: 'FilterButton',
  components: {
    SearchFilterInput: defineAsyncComponent(() => import('./types/input')),
    SearchFilterNumberInput: defineAsyncComponent(
      () => import('./types/number-input')
    ),
    SearchFilterCardInput: defineAsyncComponent(
      () => import('./types/card-input')
    ),
    SearchFilterIpInput: defineAsyncComponent(() => import('./types/ip-input')),
    SearchFilterExpiryDateInput: defineAsyncComponent(
      () => import('./types/expiry-date-input')
    ),
    SearchFilterCurrencyInput: defineAsyncComponent(
      () => import('./types/currency-input')
    ),
    SearchFilterCurrencySelect: defineAsyncComponent(
      () => import('./types/currency-select')
    ),
    SearchFilterDaterange: defineAsyncComponent(
      () => import('./types/daterange')
    ),
    SearchFilterDate: defineAsyncComponent(() => import('./date-wrapper.vue')),
    SearchFilterSelect: defineAsyncComponent(() => import('./types/select')),
    SearchFilterSlider: defineAsyncComponent(() => import('./types/slider')),
    SearchFilterRange: defineAsyncComponent(() => import('./types/range')),
    SearchResourceSelect: defineAsyncComponent(
      () => import('./types/resource-select')
    ),
    SearchFilterMultiselect: defineAsyncComponent(
      () => import('./types/multiselect')
    ),
    SearchFilterSwitch: defineAsyncComponent(() => import('./types/switch')),
    SearchRemoteSearchSelect: defineAsyncComponent(() => import('./types/rsc')),
    SearchCheckbox: defineAsyncComponent(() => import('./types/checkbox')),
    SearchStockSelect: defineAsyncComponent(
      () => import('./types/stock-select')
    ),
    ArrowUpIcon,
    ArrowDownIcon,
    EqualizerIcon,
    ArrowRightIcon
  },
  props: {
    locale: {
      type: String,
      default: 'en'
    },
    modelValue: {
      type: Object,
      required: false,
      default: () => ({})
    },
    filters: {
      type: Array,
      required: true
    },
    textFieldName: {
      type: String,
      required: false,
      default: 'q'
    },
    width: {
      type: Number,
      default: 0
    },
    submitOnEnter: {
      type: Boolean,
      required: false,
      default: false
    },
    submitOnItem: {
      type: Boolean,
      required: false,
      default: false
    },
    submitOnItemRemove: {
      type: Boolean,
      required: false,
      default: false
    },
    submitOnReset: {
      type: Boolean,
      required: false,
      default: false
    },
    closeOnSubmit: {
      type: Boolean,
      required: false,
      default: true
    },
    dynamicFilters: {
      type: Boolean,
      default: false
    },
    clickAwayListener: {
      type: Boolean,
      required: false,
      default: true
    },
    tableIdentifier: {
      type: String,
      default: ''
    },
    showPillFilter: {
      type: Boolean,
      default: false
    },
    filterCategories: {
      type: Object,
      default: () => ({})
    }
  },
  emits: [
    'update:modelValue',
    'filter',
    'submit',
    'toggled-dropdown',
    'close-dropdown',
    'reset',
    'update-filter'
  ],
  setup() {
    const dropdownItemsRef = ref(null)
    const { maxHeightStyle, begin, end } =
      useDropdownViewportMaxHeight(dropdownItemsRef)
    return {
      dropdownItemsRef,
      dropdownItemsMaxHeightStyle: maxHeightStyle,
      beginDropdownMaxHeight: begin,
      endDropdownMaxHeight: end
    }
  },
  data() {
    return {
      input: '',
      invalidFilters: false,
      dropdownOpen: false,
      mousedownInPopper: false,
      tagsObject: this.normaliseIncomingFilters(),
      categoryState: {},
      vcoConfig: {
        handler: this.closeOnOutsideClick,
        middleware: this.vcoMiddleware
      }
    }
  },
  computed: {
    categorizedFilters() {
      const grouped = {}
      for (const category in this.filterCategories) {
        const categoryFilters = this.filters.filter((f) =>
          this.filterCategories[category].includes(f.name)
        )
        if (categoryFilters.length) {
          grouped[category] = categoryFilters
        }
      }
      return grouped
    },
    groupedFilters() {
      const filters = this.filters.filter((item) => item.type || item.render)
      const groupSize = 5
      const groups = []
      for (let i = 0; i < filters.length; i += groupSize) {
        groups.push(filters.slice(i, i + groupSize))
      }
      return groups
    },
    i18nConfig() {
      return {
        inputPlaceholder: this.$t(
          'components.th_datatable.search_filter.search.placeholder'
        ),
        selectPlaceholder: this.$t(
          'components.th_datatable.search_filter.select.placeholder'
        ),
        searchButtonText: this.$t(
          'components.th_datatable.search_filter.search.button'
        ),
        resetButtonText: this.$t(
          'components.th_datatable.search_filter.search.reset'
        )
      }
    },
    inputFieldStyle() {
      const obj = {}
      if (this.width && this.width > 350) {
        return { ...obj, width: `${parseFloat(this.width) + 60}px` }
      }
      return obj
    },
    containerStyle() {
      if (this.width && this.width > 350) {
        return { width: `${this.width}px` }
      }
      return {}
    },
    searchDisabled() {
      return !Object.keys(this.tagsObject).length
    }
  },
  watch: {
    categorizedFilters: {
      handler(newVal) {
        const initialState = {}
        Object.keys(newVal).forEach((category) => {
          if (this.categoryState[category] === undefined) {
            initialState[category] = category.includes('common')
          } else {
            initialState[category] = this.categoryState[category]
          }
        })
        this.categoryState = initialState
      },
      immediate: true
    },
    filters: {
      handler: function (newValues, oldValues) {
        if (this.dynamicFilters && !compare(newValues, oldValues)) {
          this.tagsObject = this.normaliseIncomingFilters()
        }
      }
    },
    tagsObject: {
      handler: function (newValues) {
        this.$emit('update:modelValue', newValues)
      },
      deep: true
    },
    modelValue(newValues, oldValues) {
      if (!compare(newValues, oldValues)) {
        this.tagsObject = this.normaliseIncomingFilters()
      }
    }
  },
  mounted() {
    document.addEventListener('mousedown', this.trackMousedownOrigin, true)
  },
  beforeUnmount() {
    document.removeEventListener('mousedown', this.trackMousedownOrigin, true)
  },
  methods: {
    toggleCategory(category) {
      this.categoryState[category] = !this.categoryState[category]
    },
    shouldRenderComponent(filter) {
      return this.tagsObject[filter.name] && this.componentMapping(filter.type)
    },
    componentMapping(type) {
      const mapping = {
        input: 'SearchFilterInput',
        'number-input': 'SearchFilterNumberInput',
        'ip-input': 'SearchFilterIpInput',
        'card-input': 'SearchFilterCardInput',
        'expiry-date-input': 'SearchFilterExpiryDateInput',
        'currency-input': 'SearchFilterCurrencyInput',
        'currency-select': 'SearchFilterCurrencySelect',
        daterange: 'SearchFilterDaterange',
        date: 'SearchFilterDate',
        select: 'SearchFilterSelect',
        multiselect: 'SearchFilterMultiselect',
        slider: 'SearchFilterSlider',
        range: 'SearchFilterRange',
        'resource-select': 'SearchResourceSelect',
        switch: 'SearchFilterSwitch',
        'remote-search-select': 'SearchRemoteSearchSelect',
        checkbox: 'SearchCheckbox',
        'stock-select': 'SearchStockSelect'
      }
      return mapping[type] || null
    },
    normaliseIncomingFilters() {
      return this.filters.reduce(
        (incomingFilters, item) => {
          incomingFilters[item.name] = {
            value: null,
            ...item,
            ...incomingFilters[item.name]
          }
          return incomingFilters
        },
        {
          [this.textFieldName]: { label: null, value: null },
          ...this.modelValue
        }
      )
    },
    dispatchFilter() {
      const filter = {}
      Object.entries(this.tagsObject)
        .filter((item) => {
          return !(
            item.value === null ||
            item.value === '' ||
            item[1].value === null ||
            item[1].value === '' ||
            this.isEmptyArray(item[1].value)
          )
        })
        .forEach((item) => {
          filter[item[0]] = item[1].value
        })
      this.$emit('filter', filter, this.tagsObject)
      if (this.submitOnItem) {
        this.handleSubmit()
      }
    },
    toggleDropdown() {
      this.dropdownOpen = !this.dropdownOpen
      if (this.dropdownOpen) {
        this.beginDropdownMaxHeight()
      } else {
        this.endDropdownMaxHeight()
        this.$emit('close-dropdown')
      }
      this.$emit('toggled-dropdown')
    },
    handleSubmit() {
      if (this.invalidFilters) return
      if (this.closeOnSubmit) this.closeDropdown()
      const trimmedTagsObject = this.prepareTagsObject(this.tagsObject)
      this.$emit('submit', trimmedTagsObject)
      return trimmedTagsObject
    },
    handleInvalidFilters(invalid) {
      this.invalidFilters = invalid
    },
    prepareTagsObject(tagsObject) {
      const trimmed = { ...tagsObject }
      // remove trailing whitespace in all nested levels
      function trimInputs(obj) {
        for (const key in obj) {
          if (obj[key] !== null && typeof obj[key] === 'object') {
            // works on objects and arrays
            obj[key] = trimInputs(obj[key])
          }
          if (typeof obj[key] === 'string') {
            obj[key] = obj[key].trim()
          }
        }
        return obj
      }
      trimInputs(trimmed)
      return trimmed
    },
    handleSearchClick() {
      this.handleSubmit()
    },
    reset() {
      this.tagsObject = this.normaliseIncomingFilters()
      this.tagsObject = mapValues(this.tagsObject, (value) => {
        value.value = value.default ?? null
        return value
      })
      this.$emit('reset')
      this.$emit('submit', this.handleSubmit())
    },
    closeDropdown() {
      this.endDropdownMaxHeight()
      this.dropdownOpen = false
      this.$emit('close-dropdown')
    },
    handleEnter() {
      if (this.submitOnEnter) {
        this.handleSubmit()
      }
    },
    closeOnOutsideClick() {
      if (this.dropdownOpen) {
        this.closeDropdown()
      }
    },
    isEmptyArray(value) {
      return Array.isArray(value) && !value.length
    },
    handleInitialFetch(filter, data) {
      this.tagsObject[filter.name].originalData = data
      this.$emit('update-filter', this.tagsObject[filter.name])
    },
    trackMousedownOrigin(event) {
      this.mousedownInPopper = Boolean(event.target?.closest?.('.el-popper'))
    },
    vcoMiddleware(event) {
      if (this.mousedownInPopper) {
        return false
      }
      if (event.target?.closest?.('.el-popper')) {
        return false
      }
      return true
    }
  }
}
</script>
<style scoped>
* {
  border-color: var(--border-color);
}
.th-container {
  min-width: 485px;
  max-width: 600px;
  margin-top: 5px;
  z-index: 2100;
}
.th-container-items {
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 250px);
}
.filter-items {
  min-height: 0;
}
.th-container-gap {
  top: -12px;
  left: -1px;
}
</style>
